import { ReactNode, useEffect, useState } from "react";

import {
	ActionIcon,
	Avatar,
	Box,
	Button,
	Divider,
	Group,
	Image,
	Menu,
	NavLink,
	SegmentedControl,
	Stack,
	Text,
	UnstyledButton,
	useMantineColorScheme,
} from "@mantine/core";
import {
	IconChartLine,
	IconChevronDown,
	IconCircle,
	IconCircleCheckFilled,
	IconCirclePlus,
	IconDotsVertical,
	IconLayoutGrid,
	IconLogout,
	IconMoon,
	IconMusic,
	IconPencil,
	IconPlaylist,
	IconSun,
	IconUser,
	IconUsersGroup,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import logo from "@/Assets/images/logo.png";
import { useAuth } from "@/Components/Auth/AuthProvider";
import ManageGroupModal from "@/Components/Groups/ManageGroupModal";
import {
	useMyGroupsQuery,
	useUserLimitsQuery,
} from "@/Components/Groups/Queries";
import { confirm } from "@/Components/Utils/confirm";
import LimitReachedButton from "@/Components/Utils/LimitReachedButton";
import { notifyError, notifySuccess } from "@/Components/Utils/notifications";
import { useApiHelper } from "@/contexts";
import { useUserGroups } from "@/hooks/useUserGroups";
import type { UserGroup } from "@/types/user";

const HEADWAY_CONFIG = {
	selector: "#header-brand",
	account: "7QAKA7",
	translations: {
		title: "Nouveautés",
		footer: "Voir plus 👉",
		readMore: "Voir plus 👉",
	},
};

declare global {
	interface Window {
		Headway?: { init: (config: typeof HEADWAY_CONFIG) => void };
	}
}

function SideBarLink({
	to,
	label,
	icon,
	active,
	onNavigate,
}: {
	to: string;
	label: string;
	icon: ReactNode;
	active: boolean;
	onNavigate?: () => void;
}) {
	return (
		<NavLink
			component={Link}
			to={to}
			label={label}
			leftSection={icon}
			active={active}
			onClick={onNavigate}
			color="hooky"
			styles={{
				root: {
					borderRadius: "var(--mantine-radius-md)",
					...(active && {
						background:
							"linear-gradient(90deg, rgba(61, 125, 245, 0.22), rgba(61, 125, 245, 0.05))",
						boxShadow: "inset 2px 0 0 var(--mantine-color-hooky-6)",
					}),
				},
				label: { fontSize: "0.875rem", fontWeight: 500 },
			}}
			c={active ? "var(--hooky-heading)" : "var(--hooky-muted)"}
		/>
	);
}

function SectionLabel({ children }: { children: ReactNode }) {
	return (
		<Text
			fz={11}
			fw={600}
			tt="uppercase"
			px="sm"
			pt="lg"
			pb={6}
			c="var(--hooky-muted-2)"
			style={{ letterSpacing: "0.14em" }}
		>
			{children}
		</Text>
	);
}

// The Headway changelog widget anchors on the brand (#header-brand).
export default function SideBar({ onNavigate }: { onNavigate?: () => void }) {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const queryClient = useQueryClient();
	const apiHelper = useApiHelper();
	const auth = useAuth();
	const { colorScheme, toggleColorScheme } = useMantineColorScheme();

	const { data: groups } = useMyGroupsQuery();
	const { data: userLimits } = useUserLimitsQuery();
	const {
		getLoggedGroupName,
		getLoggedGroupIsPublic,
		isGroupOwner,
		ownedGroupsCount,
	} = useUserGroups();

	const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [modalOpened, setModalOpened] = useState(false);

	useEffect(() => {
		if (typeof window.Headway !== "undefined") {
			window.Headway.init(HEADWAY_CONFIG);
		}
	}, []);

	if (!auth.user) {
		return null;
	}

	const user = auth.user;
	const language = i18n.language.startsWith("fr") ? "fr" : "en";
	const initials = `${user.firstname?.[0] ?? ""}${
		user.lastname?.[0] ?? ""
	}`.toUpperCase();

	const groupLimitReached = (() => {
		if (!groups || !userLimits) return false;
		if (userLimits.maxGroups === null) return false;
		const createdGroupsCount =
			userLimits.createdGroupsCount ?? ownedGroupsCount();
		return createdGroupsCount >= userLimits.maxGroups;
	})();

	const refreshGroups = async () => {
		await auth.getAuth(true);
		queryClient.invalidateQueries({ queryKey: ["myGroups"] });
		queryClient.invalidateQueries({ queryKey: ["userGroups"] });
	};

	const openManageGroupModal = async (groupUuid: string) => {
		try {
			const group = await apiHelper.users.getUserGroup(groupUuid);
			setSelectedGroup(group);
			setIsEditMode(true);
			setModalOpened(true);
		} catch (error) {
			console.error("Error loading group:", error);
			notifyError(t("Error"), t("Error while loading the group"));
		}
	};

	const openAddGroupModal = () => {
		setSelectedGroup(null);
		setIsEditMode(false);
		setModalOpened(true);
	};

	const leaveGroup = async (groupUuid: string) => {
		const group = groups?.find((g) => g.uuid === groupUuid);
		if (!group) return;

		const currentUserUuid = user.user_uuid || user.sub;
		const isOwner = group.owners?.some(
			(owner) =>
				owner.uuid === currentUserUuid || owner.sub === currentUserUuid
		);
		const isSoleOwner = isOwner && group.owners?.length === 1;

		if (isSoleOwner) {
			await confirm({
				title: t("Action not possible"),
				message: t(
					"You cannot leave the group because you are the sole owner. Add another owner or delete the group."
				),
				type: "warning",
				confirmLabel: t("confirm").capfirst(),
			});
			return;
		}

		const confirmed = await confirm({
			title: t("Leave the group"),
			message: t('Are you sure you want to leave the group "{{name}}"?', {
				name: group.name,
			}),
			type: "warning",
			confirmLabel: t("Leave"),
			cancelLabel: t("cancel").capfirst(),
		});
		if (!confirmed) return;

		try {
			await apiHelper.users.leaveGroup(groupUuid);
			await refreshGroups();
			notifySuccess(t("Success"), t("You left the group successfully"));
		} catch (error) {
			console.error("Error leaving group:", error);
			const message = (
				error as { response?: { data?: { message?: string } } }
			)?.response?.data?.message;
			notifyError(
				t("Error"),
				message || t("Error while leaving the group")
			);
		}
	};

	const { pathname } = location;

	return (
		<Stack h="100%" gap={0} p="sm" data-testid="sidebar">
			<Group
				component={Link}
				{...{ to: "/" }}
				gap="xs"
				px="xs"
				pb="lg"
				id="header-brand"
				style={{ textDecoration: "none", color: "inherit" }}
			>
				<Image src={logo} alt="logo" h={36} w="auto" radius="sm" />
				<Text fw={700} fz={21} style={{ letterSpacing: "-0.01em" }}>
					Hooky
				</Text>
			</Group>

			{/* Public library groups only share musics/playlists — no Scenes nav. */}
			{!getLoggedGroupIsPublic() && (
				<SideBarLink
					to="/scenes"
					label={t("Scenes")}
					icon={<IconLayoutGrid size={20} stroke={1.8} />}
					active={
						pathname.startsWith("/scenes") ||
						pathname.startsWith("/scene/")
					}
					onNavigate={onNavigate}
				/>
			)}
			<SideBarLink
				to="/musics"
				label={t("Library")}
				icon={<IconMusic size={20} stroke={1.8} />}
				active={pathname.startsWith("/musics")}
				onNavigate={onNavigate}
			/>
			<SideBarLink
				to="/playlists"
				label={t("Playlists")}
				icon={<IconPlaylist size={20} stroke={1.8} />}
				active={
					pathname.startsWith("/playlists") ||
					pathname.startsWith("/playlist/")
				}
				onNavigate={onNavigate}
			/>

			{auth.isAdmin && (
				<>
					<SectionLabel>{t("Insights")}</SectionLabel>
					<SideBarLink
						to="/admin/beta-registration"
						label={t("Beta signups")}
						icon={<IconChartLine size={20} stroke={1.8} />}
						active={pathname.startsWith("/admin/beta-registration")}
						onNavigate={onNavigate}
					/>
					<SideBarLink
						to="/admin/users/manage"
						label={t("Users")}
						icon={<IconUsersGroup size={20} stroke={1.8} />}
						active={pathname.startsWith("/admin/users/manage")}
						onNavigate={onNavigate}
					/>
				</>
			)}

			<Box style={{ flex: 1 }} />

			{auth.isImpersonating && (
				<Button
					variant="outline"
					color="warning"
					fullWidth
					mb="sm"
					leftSection={<IconUser size={16} />}
					onClick={() => auth.loginBackAsAdmin()}
					title={t("Return to the administrator account")}
				>
					{t("Back to my account")}
				</Button>
			)}

			<Menu position="top" closeOnItemClick={false} width={260}>
				<Menu.Target>
					<UnstyledButton
						mt="sm"
						p="sm"
						data-testid="workspace-switcher"
						style={{
							border: "1px solid var(--hooky-border)",
							borderRadius: "var(--mantine-radius-md)",
							background: "var(--hooky-surface)",
						}}
					>
						<Text
							fz={10}
							fw={600}
							tt="uppercase"
							c="var(--hooky-muted-2)"
							mb={5}
							style={{ letterSpacing: "0.11em" }}
						>
							{t("Workgroup")}
						</Text>
						<Group justify="space-between" gap="xs" wrap="nowrap">
							<Text fw={600} fz="md" truncate>
								{getLoggedGroupName()}
							</Text>
							<IconChevronDown
								size={15}
								color="var(--hooky-muted-2)"
							/>
						</Group>
					</UnstyledButton>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item
						leftSection={
							user.user_uuid ? (
								<IconCircle size={16} />
							) : (
								<IconCircleCheckFilled size={16} />
							)
						}
						onClick={() => auth.loginBackAsUser()}
					>
						{t("Personal")}
					</Menu.Item>
					{groups?.map((group) => (
						<Group
							key={group.uuid}
							justify="space-between"
							wrap="nowrap"
							gap={0}
						>
							<Menu.Item
								style={{ flex: 1 }}
								leftSection={
									group.uuid !== user.sub ? (
										<IconCircle size={16} />
									) : (
										<IconCircleCheckFilled size={16} />
									)
								}
								onClick={() => auth.loginAsGroup(group.uuid)}
							>
								{group.name}
							</Menu.Item>
							<Menu
								position="right-start"
								withinPortal
								closeOnItemClick
							>
								<Menu.Target>
									<ActionIcon
										size="sm"
										title={t("Group options")}
										onClick={(e) => e.stopPropagation()}
									>
										<IconDotsVertical size={14} />
									</ActionIcon>
								</Menu.Target>
								<Menu.Dropdown>
									{isGroupOwner(group.uuid) && (
										<Menu.Item
											leftSection={
												<IconPencil size={14} />
											}
											onClick={() =>
												openManageGroupModal(group.uuid)
											}
										>
											{t("Manage the group")}
										</Menu.Item>
									)}
									<Menu.Item
										color="danger"
										leftSection={<IconLogout size={14} />}
										onClick={() => leaveGroup(group.uuid)}
									>
										{t("Leave the group")}
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						</Group>
					))}
					<Divider my={4} />
					{!groupLimitReached ? (
						<Menu.Item
							leftSection={<IconCirclePlus size={16} />}
							onClick={openAddGroupModal}
						>
							{t("Create a group")}
						</Menu.Item>
					) : (
						<Box px="sm" py={4}>
							<LimitReachedButton
								limitType={t("groups")}
								current={ownedGroupsCount()}
								max={userLimits?.maxGroups || 0}
								label={t("Group limit reached")}
							/>
						</Box>
					)}
				</Menu.Dropdown>
			</Menu>

			<Group gap="xs" mt="sm" wrap="nowrap">
				<SegmentedControl
					fullWidth
					style={{ flex: 1 }}
					value={language}
					onChange={(value) => i18n.changeLanguage(value)}
					data={[
						{ value: "fr", label: "🇫🇷 FR" },
						{ value: "en", label: "🇬🇧 EN" },
					]}
				/>
				<ActionIcon
					variant="default"
					size={36}
					aria-label={t("Toggle theme")}
					onClick={() => toggleColorScheme()}
				>
					{colorScheme === "dark" ? (
						<IconSun size={18} />
					) : (
						<IconMoon size={18} />
					)}
				</ActionIcon>
			</Group>

			<Group
				gap="sm"
				pt="sm"
				px={4}
				wrap="nowrap"
				data-testid="user-menu"
			>
				<Avatar
					size={34}
					radius="xl"
					variant="gradient"
					gradient={{ from: "#3d7df5", to: "#2bb6da", deg: 135 }}
					ff="monospace"
				>
					{initials}
				</Avatar>
				<Box style={{ flex: 1, minWidth: 0 }}>
					<Text fw={600} fz="md" truncate>
						{user.firstname} {user.lastname}
					</Text>
					<Text fz="sm" c="var(--hooky-muted-2)" truncate>
						{auth.isAdmin ? t("Administrator") : t("User")}
					</Text>
				</Box>
				<ActionIcon
					aria-label={t("Sign out")}
					title={t("Sign out")}
					onClick={() => auth.logout()}
				>
					<IconLogout size={18} />
				</ActionIcon>
			</Group>

			<ManageGroupModal
				opened={modalOpened}
				onClose={() => setModalOpened(false)}
				group={selectedGroup}
				isEdit={isEditMode}
				onGroupSaved={async () => {
					await refreshGroups();
					setModalOpened(false);
				}}
			/>
		</Stack>
	);
}
